/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Flex, Box, Separator } from 'chakra-v3';
import { throttle } from 'lodash';
import { Text } from './Text';

export interface StepperProps {
  number: number;
  showConnector?: boolean;
}

/**
 * Stepper component for displaying a styled number.
 *
 * Based on the Figma design for the tutorial step numbers.
 * The connector dynamically extends to the next stepper component.
 *
 * @example
 * ```tsx
 * <Stepper number={1} showConnector />
 * ```
 */
export const Stepper: React.FC<StepperProps> = ({ number, showConnector = false }) => {
  const stepperRef = useRef<HTMLDivElement>(null);
  const [connectorHeight, setConnectorHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!showConnector || !stepperRef.current) {
      setConnectorHeight(undefined);
      return;
    }

    const updateConnectorHeight = () => {
      const stepperElement = stepperRef.current;
      if (!stepperElement) return;

      // Find the next stepper element (sibling section's stepper)
      const currentSection = stepperElement.closest('[data-section-container]');
      if (!currentSection) return;

      const nextSection = currentSection.nextElementSibling as HTMLElement | null;
      if (!nextSection) return;

      const nextStepper = nextSection.querySelector('[data-stepper]') as HTMLElement | null;
      if (!nextStepper) return;

      // Calculate the distance from current stepper bottom to next stepper top
      const currentStepperBottom = stepperElement.getBoundingClientRect().bottom;
      const nextStepperTop = nextStepper.getBoundingClientRect().top;
      const distance = nextStepperTop - currentStepperBottom;

      if (distance > 0) {
        setConnectorHeight(distance);
      } else {
        setConnectorHeight(undefined);
      }
    };

    const stepperElement = stepperRef.current;
    const currentSection = stepperElement.closest('[data-section-container]');
    if (!currentSection) return;

    // Find the parent container that holds all sections (narrower scope)
    const parentContainer = currentSection.parentElement;
    if (!parentContainer) return;

    // Throttle the update function to prevent excessive calls
    const throttledUpdate = throttle(() => {
      requestAnimationFrame(updateConnectorHeight);
    }, 100);

    // Use requestAnimationFrame for initial calculation after render
    const rafId = requestAnimationFrame(() => {
      updateConnectorHeight();
    });

    // Update on window resize
    const handleResize = () => {
      requestAnimationFrame(updateConnectorHeight);
    };
    window.addEventListener('resize', handleResize);

    // Use ResizeObserver for size changes on relevant sections (more efficient)
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateConnectorHeight);
    });
    resizeObserver.observe(currentSection);

    const nextSection = currentSection.nextElementSibling as HTMLElement | null;
    if (nextSection) {
      resizeObserver.observe(nextSection);
    }

    // Use MutationObserver with narrowed scope and throttling for structural changes
    const mutationObserver = new MutationObserver(() => {
      throttledUpdate();
    });
    // Observe only the parent container instead of document.body
    mutationObserver.observe(parentContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'], // Only watch style/class changes that might affect layout
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      throttledUpdate.cancel();
    };
  }, [showConnector]);

  return (
    <Box position="relative" ref={stepperRef} data-stepper>
      <Flex
        w="40px"
        h="40px"
        p="10px"
        flexDirection="column"
        borderRadius="4px"
        bg="stepper.background"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        position="relative"
        zIndex={1}
      >
        <Text fontSize="20px" fontWeight="600" color="text.primary" fontFamily="'DM Sans', sans-serif">
          {number}
        </Text>
      </Flex>
      {showConnector && (
        <Separator
          orientation="vertical"
          position="absolute"
          top="40px"
          left="50%"
          transform="translateX(-50%)"
          h={connectorHeight ? `${connectorHeight}px` : '100%'}
          borderColor="connector.default"
          borderWidth="1px"
        />
      )}
    </Box>
  );
};
